import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { MapPin, IndianRupee, Users, ShoppingCart } from "lucide-react";
import { Annotation } from "@/components/Annotation";
import { ComposableMap, Geographies, Geography as GeoShape, ZoomableGroup } from "react-simple-maps";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer, Cell
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INDIA_TOPO = "https://gist.githubusercontent.com/jbrobst/56c13bbbf9d97d187fea01ca62ea5112/raw/e388c4cae20aa53cb5090210a42ebb9b765c0a36/india_states.geojson";

const formatINR = (val) => {
  if (val >= 10000000) return `${(val / 10000000).toFixed(2)} Cr`;
  if (val >= 100000) return `${(val / 100000).toFixed(1)} L`;
  if (val >= 1000) return `${(val / 1000).toFixed(0)}K`;
  return Math.round(val);
};

const COLORS = ["#3b82f6","#10b981","#f59e0b","#8b5cf6","#f43f5e","#06b6d4","#ec4899","#84cc16","#0ea5e9","#a855f7"];

export default function GeographyPage() {
  const [geoData, setGeoData] = useState([]);
  const [hoveredState, setHoveredState] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/geography/analysis`).then((res) => {
      setGeoData(res.data.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // Build lookup by state name
  const stateMap = {};
  geoData.forEach((s) => { stateMap[s.state] = s; });

  const maxRev = geoData.length > 0 ? Math.max(...geoData.map((s) => s.revenue)) : 1;

  const getColor = (stateName) => {
    const d = stateMap[stateName];
    if (!d) return "#1e293b";
    const ratio = Math.log(d.revenue + 1) / Math.log(maxRev + 1);
    const clamped = Math.min(Math.max(ratio, 0.05), 1);
    const r = Math.round(15 + clamped * 44);
    const g = Math.round(23 + clamped * 107);
    const b = Math.round(42 + clamped * 204);
    return `rgb(${r}, ${g}, ${b})`;
  };

  const totalRevenue = geoData.reduce((s, g) => s + g.revenue, 0);
  const totalCustomers = geoData.reduce((s, g) => s + g.customers, 0);
  const top5 = geoData.slice(0, 5);
  const top5Revenue = top5.reduce((s, g) => s + g.revenue, 0);

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 skeleton-shimmer rounded" />
        <div className="h-[500px] skeleton-shimmer rounded" />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6" data-testid="geography-page">
        <div>
          <h1 className="font-space text-2xl sm:text-3xl font-bold text-slate-100 tracking-tight">
            Geography Analysis
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            State-wise customer distribution &middot; {geoData.length} states
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="glass-card rounded-md p-4" data-testid="geo-total-states">
            <p className="text-[11px] text-slate-500 uppercase tracking-wider">States Active</p>
            <p className="font-mono text-lg text-slate-200 mt-1">{geoData.length}</p>
          </div>
          <div className="glass-card rounded-md p-4" data-testid="geo-top-state">
            <p className="text-[11px] text-slate-500 uppercase tracking-wider">Top State</p>
            <p className="font-mono text-lg text-slate-200 mt-1">{geoData[0]?.state || "N/A"}</p>
          </div>
          <div className="glass-card rounded-md p-4" data-testid="geo-top5-share">
            <p className="text-[11px] text-slate-500 uppercase tracking-wider">Top 5 Revenue Share</p>
            <p className="font-mono text-lg text-emerald-400 mt-1">{totalRevenue > 0 ? ((top5Revenue / totalRevenue) * 100).toFixed(1) : 0}%</p>
          </div>
          <div className="glass-card rounded-md p-4" data-testid="geo-avg-aov">
            <p className="text-[11px] text-slate-500 uppercase tracking-wider">Avg AOV (Top 5)</p>
            <p className="font-mono text-lg text-amber-400 mt-1">INR {top5.length > 0 ? formatINR(top5.reduce((s, g) => s + g.avg_order_value, 0) / top5.length) : 0}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          {/* India Map */}
          <Card className="lg:col-span-7 border-slate-800 bg-slate-900/50 backdrop-blur-xl relative" data-testid="india-map">
            <CardHeader className="pb-2">
              <CardTitle className="font-space text-base font-semibold text-slate-200 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-blue-400" />
                Revenue Distribution Map
              </CardTitle>
            </CardHeader>
            <CardContent className="relative">
              <ComposableMap
                projection="geoMercator"
                projectionConfig={{ scale: 1000, center: [82, 22] }}
                width={600}
                height={550}
                style={{ width: "100%", height: "auto" }}
              >
                <ZoomableGroup>
                  <Geographies geography={INDIA_TOPO}>
                    {({ geographies }) =>
                      geographies.map((geo) => {
                        const name = geo.properties.ST_NM;
                        const stateData = stateMap[name];
                        return (
                          <GeoShape
                            key={geo.rsmKey}
                            geography={geo}
                            fill={getColor(name)}
                            stroke="#0f172a"
                            strokeWidth={0.5}
                            style={{
                              default: { outline: "none" },
                              hover: { outline: "none", fill: stateData ? "#3b82f6" : "#334155", cursor: "pointer" },
                              pressed: { outline: "none" }
                            }}
                            onMouseEnter={(e) => {
                              setHoveredState(name);
                              setTooltipPos({ x: e.clientX, y: e.clientY });
                            }}
                            onMouseMove={(e) => {
                              setTooltipPos({ x: e.clientX, y: e.clientY });
                            }}
                            onMouseLeave={() => setHoveredState(null)}
                          />
                        );
                      })
                    }
                  </Geographies>
                </ZoomableGroup>
              </ComposableMap>

              {/* Map Tooltip */}
              {hoveredState && (
                <div
                  className="fixed z-50 pointer-events-none bg-slate-900 border border-slate-700 rounded-md px-3 py-2.5 shadow-2xl text-xs"
                  style={{ left: tooltipPos.x + 12, top: tooltipPos.y - 10 }}
                  data-testid="map-tooltip"
                >
                  <p className="font-semibold text-slate-100 mb-1.5">{hoveredState}</p>
                  {stateMap[hoveredState] ? (
                    <>
                      <div className="flex items-center gap-2 text-slate-400">
                        <Users className="w-3 h-3" />
                        <span>Customers: <span className="text-slate-200 font-mono">{stateMap[hoveredState].customers.toLocaleString()}</span></span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-400 mt-1">
                        <ShoppingCart className="w-3 h-3" />
                        <span>Orders: <span className="text-slate-200 font-mono">{stateMap[hoveredState].orders.toLocaleString()}</span></span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-400 mt-1">
                        <IndianRupee className="w-3 h-3" />
                        <span>Revenue: <span className="text-emerald-400 font-mono">INR {formatINR(stateMap[hoveredState].revenue)}</span></span>
                      </div>
                      <div className="flex items-center gap-2 text-slate-400 mt-1">
                        <span>AOV: <span className="text-amber-400 font-mono">INR {formatINR(stateMap[hoveredState].avg_order_value)}</span></span>
                      </div>
                    </>
                  ) : (
                    <p className="text-slate-500">No data available</p>
                  )}
                </div>
              )}

              {/* Legend */}
              <div className="flex items-center gap-2 mt-2">
                <span className="text-[10px] text-slate-500">Low</span>
                <div className="flex">
                  {[0.1, 0.3, 0.5, 0.7, 0.9].map((r) => (
                    <div key={r} className="w-8 h-3" style={{
                      backgroundColor: `rgb(${Math.round(15 + r * 44)}, ${Math.round(23 + r * 107)}, ${Math.round(42 + r * 204)})`
                    }} />
                  ))}
                </div>
                <span className="text-[10px] text-slate-500">High Revenue</span>
              </div>
            </CardContent>
          </Card>

          {/* Top States Bar Chart */}
          <Card className="lg:col-span-5 border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="top-states-chart">
            <CardHeader className="pb-2">
              <CardTitle className="font-space text-base font-semibold text-slate-200 flex items-center gap-2">
                <IndianRupee className="w-4 h-4 text-emerald-400" />
                Top 10 States by Revenue
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={420}>
                <BarChart data={geoData.slice(0, 10)} layout="vertical" margin={{ left: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" horizontal={false} />
                  <XAxis type="number" tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={formatINR} />
                  <YAxis dataKey="state" type="category" width={120} tick={{ fill: '#94a3b8', fontSize: 10 }} tickLine={false} axisLine={false} />
                  <RechartsTooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0].payload;
                      return (
                        <div className="bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-xs shadow-xl">
                          <p className="font-semibold text-slate-200 mb-1">{d.state}</p>
                          <p className="text-slate-400">Revenue: <span className="text-emerald-400 font-mono">INR {formatINR(d.revenue)}</span></p>
                          <p className="text-slate-400">Customers: <span className="font-mono text-slate-200">{d.customers.toLocaleString()}</span></p>
                          <p className="text-slate-400">AOV: <span className="font-mono text-amber-400">INR {formatINR(d.avg_order_value)}</span></p>
                        </div>
                      );
                    }}
                  />
                  <Bar dataKey="revenue" name="Revenue" radius={[0, 4, 4, 0]}>
                    {geoData.slice(0, 10).map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        <Annotation>
          Top 5 states contribute {totalRevenue > 0 ? ((top5Revenue / totalRevenue) * 100).toFixed(0) : 0}% of website revenue — concentrated in metros with high digital adoption (Maharashtra, Karnataka, Tamil Nadu, UP, Delhi). The pan-India footprint across {geoData.length} states demonstrates organic brand reach. The website channel naturally over-indexes on urban, prepayment-ready customers — since we don't offer COD or returns on our D2C site. Our marketplace presence (Amazon, Flipkart) captures broader Tier-2/3 demand where COD preference is higher. This geographic skew is a feature of channel strategy, not a limitation of brand awareness.
        </Annotation>

        {/* State Data Table */}
        <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="geo-state-table">
          <CardHeader className="pb-3">
            <CardTitle className="font-space text-base font-semibold text-slate-200">
              All States Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-800">
                    <th className="text-left py-2 px-3 text-xs text-slate-400 uppercase tracking-wider font-semibold">#</th>
                    <th className="text-left py-2 px-3 text-xs text-slate-400 uppercase tracking-wider font-semibold">State</th>
                    <th className="text-right py-2 px-3 text-xs text-slate-400 uppercase tracking-wider font-semibold">Customers</th>
                    <th className="text-right py-2 px-3 text-xs text-slate-400 uppercase tracking-wider font-semibold">Orders</th>
                    <th className="text-right py-2 px-3 text-xs text-slate-400 uppercase tracking-wider font-semibold">Revenue</th>
                    <th className="text-right py-2 px-3 text-xs text-slate-400 uppercase tracking-wider font-semibold">AOV</th>
                    <th className="text-right py-2 px-3 text-xs text-slate-400 uppercase tracking-wider font-semibold">Rev Share</th>
                  </tr>
                </thead>
                <tbody>
                  {geoData.map((s, i) => (
                    <tr key={s.state} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
                      <td className="py-2 px-3 text-slate-500 font-mono text-xs">{i + 1}</td>
                      <td className="py-2 px-3 text-slate-200">{s.state}</td>
                      <td className="py-2 px-3 text-right font-mono text-slate-300">{s.customers.toLocaleString()}</td>
                      <td className="py-2 px-3 text-right font-mono text-slate-300">{s.orders.toLocaleString()}</td>
                      <td className="py-2 px-3 text-right font-mono text-emerald-400">INR {formatINR(s.revenue)}</td>
                      <td className="py-2 px-3 text-right font-mono text-amber-400">INR {formatINR(s.avg_order_value)}</td>
                      <td className="py-2 px-3 text-right font-mono text-blue-400">{((s.revenue / totalRevenue) * 100).toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  );
}
